import {
  AttachmentUploadType,
  ReceiptAttachmentModel,
} from 'src/models/receiptAttachment.models'
import {
  ReceiptVerificationStatus,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'
import { ReceiptRecoveryCase } from 'src/models/transaction.models'

export type ReceiptEvidenceAttachmentPreviewType =
  | 'image'
  | 'pdf'
  | 'in-app-upload'
  | 'transaction-upload'
  | 'email'
  | 'phone-message'

interface GetReceiptEvidenceAttachmentParams {
  receiptRecoveryCase?: ReceiptRecoveryCase
  attachments: ReceiptAttachmentModel[]
  /** A dev-only selector that only chooses an existing live attachment. */
  attachmentPreviewType?: ReceiptEvidenceAttachmentPreviewType
}

const attachmentTimestamp = (attachment: ReceiptAttachmentModel): number => {
  const verifiedAt = resolveAttachmentVerification(attachment)?.verifiedAt
  const timestamp = Date.parse(verifiedAt ?? attachment.createdAt)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const isVerifiedAttachment = (attachment: ReceiptAttachmentModel): boolean => {
  const status = resolveAttachmentVerification(attachment)?.status
  return (
    status === ReceiptVerificationStatus.Verified ||
    status === ReceiptVerificationStatus.AutoMatched
  )
}

export const getReceiptEvidenceAttachment = ({
  receiptRecoveryCase,
  attachments,
  attachmentPreviewType,
}: GetReceiptEvidenceAttachmentParams): ReceiptAttachmentModel | undefined => {
  if (receiptRecoveryCase?.recoveredAttachmentId) {
    return attachments.find(
      (attachment) =>
        attachment.id === receiptRecoveryCase.recoveredAttachmentId,
    )
  }

  if (attachmentPreviewType) {
    switch (attachmentPreviewType) {
      case 'image':
        return attachments.find((attachment) =>
          attachment.contentType?.startsWith('image/'),
        )
      case 'pdf':
        return attachments.find(
          (attachment) => attachment.contentType === 'application/pdf',
        )
      case 'in-app-upload':
        return attachments.find(
          (attachment) => attachment.uploadType === AttachmentUploadType.APP,
        )
      case 'transaction-upload':
        return attachments.find(
          (attachment) =>
            attachment.uploadType === AttachmentUploadType.TRANSACTION,
        )
      case 'email':
        return attachments.find(
          (attachment) => attachment.uploadType === AttachmentUploadType.EMAIL,
        )
      case 'phone-message':
        return attachments.find(
          (attachment) =>
            attachment.uploadType === AttachmentUploadType.PHONE_MESSAGE,
        )
    }
  }

  const verifiedAttachment = attachments
    .filter(isVerifiedAttachment)
    .reduce<
      ReceiptAttachmentModel | undefined
    >((newestVerifiedAttachment, attachment) => {
      if (!newestVerifiedAttachment) return attachment

      return attachmentTimestamp(attachment) >
        attachmentTimestamp(newestVerifiedAttachment)
        ? attachment
        : newestVerifiedAttachment
    }, undefined)

  if (verifiedAttachment) return verifiedAttachment

  return attachments.reduce<ReceiptAttachmentModel | undefined>(
    (newestAttachment, attachment) => {
      if (!newestAttachment) return attachment

      const newestAttachmentCreatedAt = Date.parse(newestAttachment.createdAt)
      const attachmentCreatedAt = Date.parse(attachment.createdAt)

      if (
        Number.isNaN(newestAttachmentCreatedAt) ||
        attachmentCreatedAt > newestAttachmentCreatedAt
      ) {
        return attachment
      }

      return newestAttachment
    },
    undefined,
  )
}
